import { ColId, Row } from "./types";

/**
 * Auto-save for the bulk onboarding grid.
 *
 * The draft is a scratch working copy of a batch that has not been uploaded
 * yet, so it is kept in the browser under the signed-in admin's own key rather
 * than on the server: it survives a refresh, an accidental tab close and a
 * browser crash, which is what the Excel style auto-save is there for, and it
 * needs no schema change and no new endpoint. It is deliberately per browser -
 * a draft started on a laptop does not follow the admin to another machine.
 */

const KEY_PREFIX = "smp.bulkOnboarding.draft.";
// Bumped when the row shape changes, so a draft saved by an older build is
// discarded rather than restored into a grid that cannot read it.
export const DRAFT_VERSION = 4;

export interface DraftPayload {
  v: number;
  savedAt: number;
  rows: Row[];
  hiddenCols: ColId[];
}

const draftKey = (owner: string) =>
  KEY_PREFIX + (owner || "anonymous").trim().toLowerCase();

/**
 * Drop every saved batch and column layout, for any owner, on this browser.
 *
 * Called at sign-out. The draft is keyed by username, so it survives one and is
 * offered back at the next - which is right for the same person returning to
 * finish a batch, and wrong for a shared machine, where the next person is
 * offered someone else's half-entered employees. It is also wrong for the same
 * person whose access has changed: rows entered with a wider scope come back
 * unusable, every country cell red, against a grid that cannot now resolve them.
 *
 * Every owner rather than just the one signing out: whoever is at the keyboard
 * is leaving, and a draft belonging to a name nobody is signed in as has no
 * chance of being finished.
 */
export const clearAllDrafts = (): void => {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && (key.startsWith(KEY_PREFIX) || key.startsWith(LAYOUT_PREFIX))) {
        doomed.push(key);
      }
    }
    // Collected first: removing inside the loop reindexes the store and skips
    // whatever moved into the slot just vacated.
    doomed.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Private mode or blocked storage - nothing was saved, nothing to clear.
  }
};

/** Strip transient state that must not be restored (errors, server verdicts). */
const stripRow = (r: Row): Row => ({
  ...r,
  qualTypes: [...(r.qualTypes || [])],
  qualifications: [...(r.qualifications || [])],
  qualified: [...(r.qualified || [])],
  custom: { ...(r.custom || {}) },
  errors: {},
  serverEmailFlag: "",
  serverError: "",
});

export const saveDraft = (
  owner: string,
  rows: Row[],
  hiddenCols: ColId[],
): number | null => {
  try {
    const payload: DraftPayload = {
      v: DRAFT_VERSION,
      savedAt: Date.now(),
      rows: rows.map(stripRow),
      hiddenCols,
    };
    window.localStorage.setItem(draftKey(owner), JSON.stringify(payload));
    return payload.savedAt;
  } catch (err) {
    // Quota exceeded or storage disabled - auto-save is a convenience, never a
    // reason to interrupt the admin mid batch.
    return null;
  }
};

export const loadDraft = (owner: string): DraftPayload | null => {
  try {
    const raw = window.localStorage.getItem(draftKey(owner));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== DRAFT_VERSION || !Array.isArray(parsed.rows))
      return null;
    if (parsed.rows.length === 0) return null;
    return {
      v: parsed.v,
      savedAt: Number(parsed.savedAt) || 0,
      rows: parsed.rows.map(stripRow),
      hiddenCols: Array.isArray(parsed.hiddenCols) ? parsed.hiddenCols : [],
    };
  } catch (err) {
    return null;
  }
};

export const clearDraft = (owner: string): void => {
  try {
    window.localStorage.removeItem(draftKey(owner));
  } catch (err) {
    // ignore - nothing to clean up if storage is unavailable
  }
};

// ----- column layout --------------------------------------------------------
//
// Column visibility is a display preference, not data, so it is stored
// separately from the draft and applied straight away when the panel opens.
// Discarding a draft therefore keeps the admin's column choices.

const LAYOUT_PREFIX = "smp.bulkOnboarding.layout.";

export interface LayoutPayload {
  hiddenCols: ColId[];
}

const layoutKey = (owner: string) =>
  LAYOUT_PREFIX + (owner || "anonymous").trim().toLowerCase();

export const saveLayout = (owner: string, layout: LayoutPayload): void => {
  try {
    window.localStorage.setItem(layoutKey(owner), JSON.stringify(layout));
  } catch (err) {
    // ignore - the layout falls back to the default order next time
  }
};

export const loadLayout = (owner: string): LayoutPayload | null => {
  try {
    const raw = window.localStorage.getItem(layoutKey(owner));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;
    return {
      hiddenCols: Array.isArray(parsed.hiddenCols) ? parsed.hiddenCols : [],
    };
  } catch (err) {
    return null;
  }
};

/** "14:32" style stamp for the "Saved <time>" indicator. */
export const formatSavedAt = (ts: number): string => {
  if (!ts) return "";
  const d = new Date(ts);
  const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
  return pad(d.getHours()) + ":" + pad(d.getMinutes());
};
